import AuthService from '../../common/services/AuthService';
import Logger from '../../utils/logging/Logger';
import { AppRoutingParams } from '../models/AppRoutingParams';
import TrackingService from '../../tracking/services/TrackingService';
import { debounce, isEqual } from 'lodash';
import {
  lmaReduxStore,
  RootState,
  selectActiveView,
  selectDashboardRoutingParams,
  selectOverviewRoutingParams,
  selectTourDetailsRoutingParams,
  updateFromRoutingParams,
} from '@redux';
import { ToolkitStore } from '@reduxjs/toolkit/dist/configureStore';
import { ConfigKey, getEnvVariable } from '@legacy-modules/common/services/EnvironmentConfigurationService';

export default class DeepLinkService {
  store: ToolkitStore<RootState>;
  logger: Logger = Logger.getInstance('DeepLinkService');
  storeUnsubscribe: Function | null | undefined = null;
  lastKnownRoutingParams;
  lastKnownRoutingHash: string | null | undefined = null;

  constructor(private readonly authService: AuthService, private readonly trackingService: TrackingService) {
    this.store = lmaReduxStore;
  }

  async init() {
    this.onStoreChanged = debounce(this.onStoreChanged.bind(this), 1000);
    this.storeAndUpdateHash = this.storeAndUpdateHash.bind(this);
    this.storeUnsubscribe = this.store.subscribe(this.onStoreChanged);
    window.addEventListener('popstate', this.processUrlParams.bind(this));
  }

  destruct() {
    if (this.storeUnsubscribe) {
      this.storeUnsubscribe();
    }
  }

  async getCurrentHash(): Promise<string> {
    return await this.storeAndUpdateHash();
  }

  private onStoreChanged() {
    if (this.isStoreChanged()) {
      this.storeAndUpdateHash();
    }
  }

  private isStoreChanged(): boolean {
    const routingParams = this.getAppRoutingParams();
    return !isEqual(this.lastKnownRoutingParams, routingParams);
  }

  private async storeAndUpdateHash(): Promise<string> {
    try {
      const routingParams = this.getAppRoutingParams();
      if (isEqual(this.lastKnownRoutingParams, routingParams)) {
        return;
      } else {
        this.logger.debug('Updating routingParams', routingParams);
        this.trackView();
        const uuid: string = await this.storeRoutingParameters(routingParams);
        this.lastKnownRoutingParams = routingParams;
        this.updateHash(uuid);
        this.lastKnownRoutingHash = uuid;
      }
    } catch (e) {
      this.logger.error('failed on store', e);
    }
    return Promise.resolve(this.lastKnownRoutingHash);
  }

  private updateHash(uuid: string) {
    const urlParams = new URLSearchParams(window.location.search);
    Array.from(urlParams.keys())
      .filter((k) => !['debug', 'digilabTourDetails', 'barcode'].includes(k))
      .forEach((k) => urlParams.delete(k));
    urlParams.set('v', uuid);
    window.history.pushState({}, document.title, `${location.pathname}?${urlParams.toString()}`);
  }

  private trackView() {
    const viewData = { ...this.getAppRoutingParams() };
    if (!viewData.overview) viewData.overview = null;
    if (!viewData.tourDetails) viewData.tourDetails = null;
    if (!viewData.dashboard) viewData.dashboard = null;
    this.trackingService.sendEvent('last-mile-analytics.view', {
      viewData: viewData,
    });
  }

  private async storeRoutingParameters(routingParams: AppRoutingParams): Promise<string> {
    const payload = JSON.stringify(routingParams);
    this.logger.debug('Store routing params.', payload);

    try {
      const url = new URL(`${getEnvVariable(ConfigKey.HTTP_API_URL)}/ui-state`);
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          token: this.store.getState().auth.tokenId,
        },
        body: payload,
      });
      const data = await response.json();
      this.logger.debug('Stored routing params.', data.uuid);
      return data.uuid;
    } catch (e) {
      this.logger.error('Unexpected response from api.', e);
      throw new Error('Unexpected response from api:' + e);
    }
  }

  private async fetchRoutingParams(uuid: string): Promise<AppRoutingParams> {
    try {
      const url = new URL(`${getEnvVariable(ConfigKey.HTTP_API_URL)}/ui-state`);
      url.searchParams.append('uuid', uuid);
      const response = await fetch(url, {
        headers: {
          token: this.store.getState().auth.tokenId,
        },
      });
      const data = await response.json();
      this.logger.debug('Fetched routing params.', data.content);
      return JSON.parse(data.content);
    } catch (e) {
      this.logger.error('Unexpected response from api.', e);
      throw new Error('Unexpected response from api: ' + e);
    }
  }

  private getAppRoutingParams(): AppRoutingParams {
    const viewMode = selectActiveView(lmaReduxStore.getState());
    const params = {
      viewMode,
      overview: selectOverviewRoutingParams(lmaReduxStore.getState()),
      tourDetails: selectTourDetailsRoutingParams(lmaReduxStore.getState()),
      dashboard:
        viewMode === 'dashboard' || viewMode === 'tourDetails'
          ? selectDashboardRoutingParams(lmaReduxStore.getState())
          : null,
    };
    return params;
  }

  private async applyRoutingParamsId(uuid: string): Promise<AppRoutingParams> {
    const routingParams = await this.fetchRoutingParams(uuid);
    await this.applyRoutingParams(routingParams);
    return routingParams;
  }

  private async applyRoutingParams(routingParams: AppRoutingParams) {
    if (
      !routingParams.viewMode ||
      !this.authService.can(`ui.general.view-modes.${routingParams.viewMode}`) ||
      routingParams.viewMode === 'loading'
    ) {
      if (this.authService.can('ui.general.view-modes.dashboard')) {
        routingParams.viewMode = 'dashboard';
      } else {
        routingParams.viewMode = 'overview';
      }
    }
    lmaReduxStore.dispatch(updateFromRoutingParams(routingParams));
    this.logger.info('Routing params set.', routingParams);
  }

  async processUrlParams(): Promise<void> {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has('v')) {
        await this.applyRoutingParamsId(urlParams.get('v'));
      } else {
        this.applyDefaults();
      }
    } catch (e) {
      this.logger.error('Could not load routing params.', e);
      this.applyDefaults();
    }
  }

  async applyDefaults() {
    this.storeAndUpdateHash();
  }
}
